import { useState, useEffect, useRef, useCallback } from 'react';
import { getProcessingReportsCount } from '@services/report.service';

const POLL_INTERVAL_MS = 20000;

function useProcessingReportsPoller() {
    const [processingCount, setProcessingCount] = useState<number>(0);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isActiveRef = useRef<boolean>(false);
    const inFlightRef = useRef<boolean>(false);

    const fetchCount = useCallback(async () => {
        if (inFlightRef.current) {
            return;
        }

        inFlightRef.current = true;

        try {
            const response = await getProcessingReportsCount();
            setProcessingCount(response.data?.count ?? 0);
        } catch (error) {
            console.error('Failed to fetch processing reports count', error);
        } finally {
            inFlightRef.current = false;
        }
    }, []);

    const scheduleNext = useCallback(async () => {
        if (!isActiveRef.current) {
            return;
        }

        await fetchCount();

        if (!isActiveRef.current) {
            return;
        }

        timeoutRef.current = setTimeout(scheduleNext, POLL_INTERVAL_MS);
    }, [fetchCount]);

    const startPolling = useCallback(() => {
        if (isActiveRef.current) {
            return;
        }

        isActiveRef.current = true;
        scheduleNext();
    }, [scheduleNext]);

    const stopPolling = useCallback(() => {
        isActiveRef.current = false;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, [stopPolling]);

    return { processingCount, startPolling, stopPolling, fetchCount, setProcessingCount };
}

export default useProcessingReportsPoller;
